import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import MoreVertIcon from '@mui/icons-material/MoreVertRounded';
import PlayIcon from '@mui/icons-material/PlayArrowRounded';
import SearchIcon from '@mui/icons-material/Search';
import StopIcon from '@mui/icons-material/StopRounded';
import { CircularProgress, MenuItem } from '@mui/material';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation } from 'react-router';
import { useListDecisionTreesQuery } from '../../../../generated/core-api/graphql-react-query';
import useGlobalStoreContext from '../../../Contexts/GlobalContext/Contexts/GlobalStoreContext/useGlobalStoreContext';
import useAudioDeviceConfig from '../../../Hooks/audio/useAudioDeviceConfig';
import useHistory from '../../../Hooks/useHistory/useHistory';
import useSessionContext from '../../../LiveContext/SessionContextProvider/useSessionContext';
import { useOverflowContext } from '../Overflow/OverflowContext';
import OverflowItem from '../Overflow/OverflowItem';
import ContextSelector from './Components/ContextSelector';
import DefaultAppBarButton from './DefaultAppBarButton/styles';
import StyledBox from './styles';

function SessionIcon() {
  const { session, sessionLoading } = useSessionContext();
  if (sessionLoading) return <CircularProgress size={24} sx={{ color: 'white' }} />;
  if (session) return <StopIcon />;
  return <PlayIcon />;
}

function SearchMenuComponent() {
  const { setAnchorEl } = useOverflowContext();

  const { t } = useTranslation();
  return (
    <MenuItem
      onClick={() => {
        setAnchorEl(null);
      }}
    >
      <SearchIcon />
      {t('home-page.footer-toolbar.search')}
    </MenuItem>
  );
}

function DecisionTreeMenuComponent() {
  const { setAnchorEl } = useOverflowContext();
  const { t } = useTranslation();
  return (
    <MenuItem
      onClick={() => {
        setAnchorEl(null);
      }}
    >
      <AccountTreeOutlinedIcon />
      {t('home-page.footer-toolbar.decision-tree')}
    </MenuItem>
  );
}

export default function Buttons() {
  const { projectId: [projectId] } = useGlobalStoreContext();
  const { t } = useTranslation();
  const {
    sessionId, session, stopSession, startSession, focusMode, setFocusMode,
  } = useSessionContext();
  const isHeadlessChrome = /\bHeadlessChrome\//.test(navigator.userAgent);
  const { disabled } = useAudioDeviceConfig();
  const location = useLocation();
  const isDecisionTreeRoute = React.useMemo(() => !!matchPath('/decision-trees/*', location.pathname), [location.pathname]);
  const projectTrees = useListDecisionTreesQuery({ input: { id: projectId ?? '' } }, { enabled: !!projectId });
  const { registerItemMenuComponent, showMoreButton, setAnchorEl } = useOverflowContext();
  const {
    navigate, goBack, hasHistory,
  } = useHistory();
  React.useEffect(() => {
    registerItemMenuComponent('search', <SearchMenuComponent key="search-menu-item" />);
    registerItemMenuComponent('decision-tree', <DecisionTreeMenuComponent key="decision-tree-menu-item" />);
  }, []);

  return (
    <StyledBox>
      <OverflowItem position="middle" id="search" priority={4}>
        <DefaultAppBarButton
          title={t('home-page.footer-toolbar.search')}
          onClick={() => {
            if (location.pathname.startsWith('/search')) {
              goBack();
            } else {
              navigate('/search');
            }
          }}
          className={clsx({ active: location.pathname === '/search' })}
          variant="contained"
        >
          <SearchIcon />
        </DefaultAppBarButton>
      </OverflowItem>
      {!!projectTrees.data?.getProject.decisionTrees?.filter((dt) => !dt.sequenceOnly).length && (
        <OverflowItem position="middle" id="decision-tree" priority={5}>
          <DefaultAppBarButton
            title={t('home-page.footer-toolbar.decision-tree')}
            className={clsx({ active: isDecisionTreeRoute })}
            onClick={() => {
              if (isDecisionTreeRoute) {
                if (hasHistory) {
                  goBack();
                } else {
                  navigate('/home/content', true);
                }
              } else {
                navigate('/decision-trees');
              }
            }}
            variant="contained"
          >
            <AccountTreeOutlinedIcon />
          </DefaultAppBarButton>
        </OverflowItem>
      )}
      {sessionId && (
        <>
          <OverflowItem position="middle" id="lock" priority={2}>
            <DefaultAppBarButton
              title={t(`home-page.footer-toolbar.${focusMode ? 'unlock' : 'lock'}-session`)}
              className={clsx({ active: !!focusMode })}
              onClick={() => { setFocusMode(); }}
              variant="contained"
            >
              {focusMode ? <LockRoundedIcon /> : <LockOpenRoundedIcon />}
            </DefaultAppBarButton>
          </OverflowItem>

          <OverflowItem position="middle" id="context" priority={6}>
            <ContextSelector />
          </OverflowItem>
        </>
      )}
      {showMoreButton && (
        <DefaultAppBarButton onClick={(e) => { setAnchorEl(e.currentTarget); }} className="more">
          <MoreVertIcon />
        </DefaultAppBarButton>
      )}
      <OverflowItem position="middle" id="session" priority={1}>
        <DefaultAppBarButton
          title={t(`home-page.footer-toolbar.${sessionId ? 'stop' : 'start'}-session`)}
          onClick={() => {
            if (sessionId) stopSession();
            else startSession();
          }}
          className={clsx('main', { stop: !!sessionId, inactiveSession: !!session?.isInactiveSession })}
          disabled={(disabled && !isHeadlessChrome) || !projectId}
          data-testid="session-button"
          variant="contained"
        >
          <SessionIcon />
        </DefaultAppBarButton>
      </OverflowItem>
    </StyledBox>
  );
}
